import React from 'react';

// Reusable Skeleton component for loading states
export const Skeleton = ({ className = '', variant = 'rectangle' }) => {
  const baseClasses = 'animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer';

  const variantClasses = {
    rectangle: 'rounded-lg',
    circle: 'rounded-full',
    text: 'rounded h-4',
    title: 'rounded h-6',
    button: 'rounded-lg h-10',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />
  );
};

// Table skeleton
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="min-w-full">
      <thead className="bg-gray-50">
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-6 py-3">
              <Skeleton className="h-4 w-24" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <td key={colIndex} className="px-6 py-4">
                {colIndex === 0 ? (
                  <div className="flex items-center space-x-3">
                    <Skeleton variant="circle" className="h-10 w-10" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                ) : (
                  <Skeleton className="h-4 w-full" />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Card skeleton
export const CardSkeleton = ({ count = 3 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton variant="circle" className="h-8 w-8" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <div className="flex space-x-2">
          <Skeleton variant="button" className="w-20" />
          <Skeleton variant="button" className="w-20" />
        </div>
      </div>
    ))}
  </div>
);

// List skeleton
export const ListSkeleton = ({ rows = 5 }) => (
  <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="p-4 flex items-center space-x-4">
        <Skeleton variant="circle" className="h-12 w-12" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton variant="button" className="w-24" />
      </div>
    ))}
  </div>
);

// Chat skeleton
export const ChatSkeleton = () => (
  <div className="flex h-full">
    {/* Chat list skeleton */}
    <div className="w-1/3 border-r border-gray-200 p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-3 p-3 rounded-lg">
          <Skeleton variant="circle" className="h-12 w-12" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
      ))}
    </div>

    {/* Chat messages skeleton */}
    <div className="flex-1 flex flex-col">
      <div className="border-b border-gray-200 p-4">
        <Skeleton className="h-6 w-40" />
      </div>
      <div className="flex-1 p-4 space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
            <Skeleton className={`h-16 ${i % 2 === 0 ? 'w-2/3' : 'w-1/2'} rounded-2xl`} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Chat messages skeleton for loading messages
export const ChatMessagesSkeleton = () => (
  <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
    <div className="space-y-6">
      {Array.from({ length: 4 }).map((_, groupIndex) => (
        <div key={groupIndex} className="space-y-3">
          {/* Date Header Skeleton */}
          <div className="flex justify-center">
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>

          {/* Messages Skeleton */}
          {Array.from({ length: 3 }).map((_, messageIndex) => (
            <div key={messageIndex} className={`flex ${messageIndex % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <div className={`px-4 py-3 rounded-2xl max-w-xs lg:max-w-md ${messageIndex % 2 === 0 ? 'rounded-bl-md' : 'rounded-br-md'}`}>
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// Email Templates skeleton
export const EmailTemplatesSkeleton = () => (
  <div className="bg-white rounded-xl shadow-sm border p-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-3 w-16 mb-1" />
            </div>
            <Skeleton variant="circle" className="h-8 w-8" />
          </div>
          <Skeleton className="h-3 w-full mb-2" />
          <Skeleton className="h-3 w-3/4 mb-4" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-20" />
            <div className="flex space-x-2">
              <Skeleton variant="circle" className="h-6 w-6" />
              <Skeleton variant="circle" className="h-6 w-6" />
              <Skeleton variant="circle" className="h-6 w-6" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Email Campaigns skeleton
export const EmailCampaignsSkeleton = () => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-24" />
          </th>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-20" />
          </th>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-16" />
          </th>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-20" />
          </th>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-16" />
          </th>
          <th className="px-6 py-3 text-left">
            <Skeleton className="h-4 w-16" />
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {Array.from({ length: 6 }).map((_, i) => (
          <tr key={i}>
            <td className="px-6 py-4">
              <div className="space-y-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-32" />
              </div>
            </td>
            <td className="px-6 py-4">
              <Skeleton className="h-6 w-16 rounded-full" />
            </td>
            <td className="px-6 py-4">
              <Skeleton className="h-4 w-20" />
            </td>
            <td className="px-6 py-4">
              <Skeleton className="h-4 w-16" />
            </td>
            <td className="px-6 py-4">
              <Skeleton className="h-4 w-24" />
            </td>
            <td className="px-6 py-4">
              <div className="flex space-x-2">
                <Skeleton variant="circle" className="h-8 w-8" />
                <Skeleton variant="circle" className="h-8 w-8" />
                <Skeleton variant="circle" className="h-8 w-8" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Statistics Overview skeleton
export const StatisticsOverviewSkeleton = () => (
  <div className="space-y-6">
    {/* Stats Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </div>
            <Skeleton variant="circle" className="h-12 w-12" />
          </div>
        </div>
      ))}
    </div>

    {/* Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <Skeleton className="h-6 w-40 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  </div>
);

// Statistics Performance Table skeleton
export const StatisticsPerformanceSkeleton = () => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200">
      <Skeleton className="h-6 w-48" />
    </div>
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {Array.from({ length: 7 }).map((_, i) => (
            <th key={i} className="px-6 py-3 text-left">
              <Skeleton className="h-4 w-20" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: 7 }).map((_, j) => (
              <td key={j} className="px-6 py-4">
                {j === 0 ? (
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ) : j === 1 ? (
                  <Skeleton className="h-6 w-16 rounded-full" />
                ) : (
                  <Skeleton className="h-4 w-16" />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Statistics Activity skeleton
export const StatisticsActivitySkeleton = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="px-6 py-4 border-b border-gray-200">
      <Skeleton className="h-6 w-48" />
    </div>
    <div className="p-6 space-y-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-3">
          <Skeleton variant="circle" className="h-10 w-10" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-3 w-20" />
        </div>
      ))}
    </div>
  </div>
);

// Statistics Unsubscribes skeleton
export const StatisticsUnsubscribesSkeleton = () => (
  <div className="space-y-6">
    {/* Stats Cards */}
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-24" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center">
              <Skeleton variant="circle" className="h-10 w-10" />
              <div className="ml-3 space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
            <div className="mt-2">
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Table */}
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <Skeleton className="h-6 w-40" />
      </div>
      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="flex space-x-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-24" />
        </div>

        {/* Table rows */}
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Skeleton variant="circle" className="h-10 w-10" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <div className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

